import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry } from 'rxjs/operators';
import { Observable } from 'rxjs';
import * as moment from 'moment';

@Injectable({
	providedIn: 'root'
})
export class VrboService {

	constructor(private http: HttpClient) { }

	saveAllProperties(): Observable<ApiResponse> {
		// @ts-ignore
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/createAllProperty`);
	}

  syncLOSAllProperties(): Observable<ApiResponse> {
    // @ts-ignore
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/lodging-rates/los`);
  }

  syncUnitAvailabilityAllProperties(): Observable<ApiResponse> {
    // @ts-ignore
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/unit-availability/update`);
  }

  syncLodgingRateLosUpdate(shortTermRentalId: number): Observable<string> {
		return this.http.get(`${environment.apiUrl}/vrbo/v3/lodging-rates/los/${shortTermRentalId}`, { responseType: 'text' }).pipe(retry({ count: 2, delay: 1500 }));
	}

  syncUnitAvailabilityUpdate(shortTermRentalId: number): Observable<string> {
    return this.http.get(`${environment.apiUrl}/vrbo/v3/unit-availability/update/${shortTermRentalId}`, { responseType: 'text' }).pipe(retry({ count: 2, delay: 1500 }));
  }

  saveVrboProperty(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/createProperty`, data);
  }

  migrateProperty(shortTermRentalId: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/property/migrate`, {
      shortTermRentalId: shortTermRentalId
    });
  }

  // Promotion APIs
  createEarlyBookingPromotion(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/early-booking`, data);
  }

  createLastMinutePromotion(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/last-minute`, data);
  }

  createNewListingDiscountPromotion(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/new-listing-discount`, data);
  }

  createMemberOnlyPromotion(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/member-only`, data);
  }

  createMobileOnlyPromotion(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/mobile-only`, data);
  }

  createLosDiscountPromotion(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/los-discount`, data);
  }

  queryPromotions(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/query`, data);
  }

  queryPromotionsDashboard(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/dashboard/query`, data);
  }

  updatePromotion(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/update`, data);
  }

  createWikiCoupon(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/wiki-coupon`, data);
  }

  updateWikiCoupon(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/wiki-coupon/update`, data);
  }

  checkPromotionEligibility(propertyId: string): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/promotions/eligibility`, {
      params: { propertyId }
    });
  }

  // Property resolution APIs for promotions
  getPropertiesByDevelopment(developmentIds: number[]): Observable<ApiResponse> {
    let params = new HttpParams();
    developmentIds.forEach(id => { params = params.append('developmentIds', id.toString()); });
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/properties/by-development-lite`, { params });
  }

  getPropertiesByLocation(locationIds: number[]): Observable<ApiResponse> {
    let params = new HttpParams();
    locationIds.forEach(id => { params = params.append('locationIds', id.toString()); });
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/properties/by-location-lite`, { params });
  }

  findAllPropertiesLite(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/properties/find-all-lite`);
  }

  getLocations(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/locations`);
  }

  // Review APIs
  fetchAllReviews(pageSize?: number, cursor?: string): Observable<ApiResponse> {
    let params = new HttpParams();
    if (pageSize) { params = params.set('pageSize', pageSize.toString()); }
    if (cursor) { params = params.set('cursor', cursor); }
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/reviews/properties/all`, { params });
  }

  fetchReviewsByProperty(shortTermRentalId: number, pageSize?: number, cursor?: string): Observable<ApiResponse> {
    let params = new HttpParams();
    if (pageSize) { params = params.set('pageSize', pageSize.toString()); }
    if (cursor) { params = params.set('cursor', cursor); }
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/reviews/property/${shortTermRentalId}`, { params });
  }

  fetchReviewById(reviewId: string, reviewIdSource?: string): Observable<ApiResponse> {
    let params = new HttpParams();
    if (reviewIdSource) { params = params.set('reviewIdSource', reviewIdSource); }
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/reviews/${reviewId}`, { params });
  }

  fetchReviewByReservation(reservationConfirmationNumber: string): Observable<ApiResponse> {
    let params = new HttpParams().set('reservationConfirmationNumber', reservationConfirmationNumber);
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/reviews/fetch-by-reservation`, null, { params });
  }

  respondToReview(reviewId: string, shortTermRentalId: number, responseText: string, locale?: string): Observable<ApiResponse> {
    let params = new HttpParams()
      .set('shortTermRentalId', shortTermRentalId.toString())
      .set('responseText', responseText);
    if (locale) { params = params.set('locale', locale); }
    return this.http.post<ApiResponse>(`${environment.apiUrl}/vrbo/v3/reviews/${reviewId}/respond`, null, { params });
  }

  fetchAggregatedReviews(propertyId: string, propertyIdSource?: string): Observable<ApiResponse> {
    let params = new HttpParams();
    if (propertyIdSource) { params = params.set('propertyIdSource', propertyIdSource); }
    return this.http.get<ApiResponse>(`${environment.apiUrl}/vrbo/v3/reviews/aggregated/${propertyId}`, { params });
  }

  updateReviewStatus(reviewId: number, status: number): Observable<ApiResponse> {
    const params = new HttpParams().set('status', status.toString());
    return this.http.patch<ApiResponse>(
      `${environment.apiUrl}/vrbo/v3/reviews/${reviewId}/status`, null, { params }
    );
  }

  // Rate Guest APIs
  createRateGuest(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/rate-guest`, data);
  }

  findRateGuestByReservationId(reservationId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/find-rate-guest-by-reservation-id/${reservationId}`);
  }
}
